import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, MessageCircle, X } from 'lucide-react'
import { C } from '../lib/tokens'
import { patientHidesShopTab } from '../lib/portalAccess'

const TOUR_VERSION = '2026-05-new-patient-flow-v2'

const TOUR_STEPS = [
  {
    id: 'welcome',
    path: '/dashboard',
    target: null,
    title: 'Welcome to KIS Rx',
    body: 'Your profile and consent are complete. This quick walkthrough shows where to upload or order labs, start your first shop request, track orders, and message the care team.',
  },
  {
    id: 'next-steps',
    path: '/dashboard',
    target: '[data-tour-id="next-steps"]',
    title: 'Recommended first steps',
    body: 'Start with labs. Upload recent results if you have them, or purchase a lab panel if you need current values. After labs are started, Shop is where you order your first product.',
  },
  {
    id: 'dashboard',
    path: '/dashboard',
    target: '[data-tour-id="dashboard"]',
    title: 'Dashboard',
    body: 'This is your home base. Check it first for the next thing your care team needs from you, including lab, visit, order, and account updates.',
  },
  {
    id: 'notifications',
    path: '/dashboard',
    target: '[data-tour-id="notifications"]',
    title: 'Notifications',
    body: 'The bell collects items that need attention. Open it when you want a quick read on new messages, lab updates, prescriptions, orders, or visit reminders.',
  },
  {
    id: 'shop',
    path: '/shop',
    target: '[data-tour-id="shop"]',
    title: 'Shop',
    body: 'Use Shop to purchase labs or start a treatment request. If you do not already have recent labs, this is usually the best next step.',
  },
  {
    id: 'visits',
    path: '/visits',
    target: '[data-tour-id="visits"]',
    title: 'Visits',
    body: 'Visits keeps upcoming appointments and join links in one place, so you can see what is scheduled and what needs to be booked.',
  },
  {
    id: 'messages',
    path: '/messages',
    target: '[data-tour-id="messages"]',
    title: 'Messages',
    body: 'Messages is where you talk with support or your care team. Use it for order questions, care questions, or anything that needs a person.',
  },
  {
    id: 'prescriptions',
    path: '/prescriptions',
    target: '[data-tour-id="prescriptions"]',
    title: 'Prescriptions',
    body: 'Prescriptions shows medication requests, refills, and care review updates after a prescription request is approved or active.',
  },
  {
    id: 'labs',
    path: '/labs',
    target: '[data-tour-id="labs"]',
    title: 'Labs',
    body: 'If you already have recent labs, upload them here so your provider can review them. If not, go to Shop and purchase the right lab panel first.',
  },
  {
    id: 'orders',
    path: '/orders',
    target: '[data-tour-id="orders"]',
    title: 'Orders',
    body: 'Orders tracks purchases, lab orders, refills, fulfillment, and payment status from request through completion.',
  },
  {
    id: 'account',
    path: '/account',
    target: '[data-tour-id="account"]',
    title: 'Account',
    body: 'Account holds your profile, documents, forms, payment methods, and verification items. Keep this current so orders and care reviews move cleanly.',
  },
  {
    id: 'help',
    path: '/help',
    target: '[data-tour-id="help"]',
    title: 'Help Center',
    body: 'Help Center has short patient guides for portal tabs, labs, BioBox, requisitions, orders, prescriptions, billing, and who to contact.',
  },
  {
    id: 'support-widget',
    path: '/dashboard',
    target: '[data-tour-id="support-widget"]',
    title: 'Support Widget',
    body: 'Use the floating Support button when you want the KIS Rx team to see what page you are on and route your request to the right place.',
    highlights: [
      'Request a product you would like available in the portal.',
      'Get help with orders, labs, billing, account access, uploads, forms, or portal questions.',
      'Report a bug when something looks broken or does not work as expected.',
      'Request a feature or improvement you would like added to the portal.',
    ],
    actionLabel: 'Open Support Widget',
    actionEvent: 'kis:portal-support:open',
    actionDetail: { view: 'home' },
  },
]

function tourStorageKey(patient) {
  const patientKey = patient?.id || patient?.email || 'portal-user'
  return `kis_rx_portal_walkthrough:${TOUR_VERSION}:${patientKey}`
}

function readTourComplete(key) {
  try {
    return window.localStorage.getItem(key) === 'complete'
  } catch {
    return false
  }
}

function writeTourComplete(key) {
  try {
    window.localStorage.setItem(key, 'complete')
  } catch {
    // Local storage can be blocked in private contexts. The tour still works.
  }
}

function visibleRectFor(selector) {
  if (!selector) return null
  const elements = Array.from(document.querySelectorAll(selector))
  for (const element of elements) {
    const rect = element.getBoundingClientRect()
    const style = window.getComputedStyle(element)
    if (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth &&
      style.visibility !== 'hidden' &&
      style.display !== 'none'
    ) {
      return {
        top: Math.max(8, rect.top - 6),
        left: Math.max(8, rect.left - 6),
        width: rect.width + 12,
        height: rect.height + 12,
      }
    }
  }
  return null
}

function panelPosition(rect, estimatedHeight = 260) {
  const width = Math.min(390, window.innerWidth - 28)
  if (!rect) {
    return {
      width,
      left: Math.max(14, (window.innerWidth - width) / 2),
      top: Math.max(18, Math.min(window.innerHeight - estimatedHeight - 14, window.innerHeight / 2 - estimatedHeight / 2)),
    }
  }

  let top = rect.top + rect.height + 16
  if (top + estimatedHeight > window.innerHeight - 14) {
    top = rect.top - estimatedHeight - 16
  }
  top = Math.max(14, Math.min(top, window.innerHeight - estimatedHeight - 14))

  let left = rect.left
  if (left + width > window.innerWidth - 14) left = window.innerWidth - width - 14
  left = Math.max(14, left)

  return { width, left, top }
}

export default function PortalWalkthrough({ patient }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [targetRect, setTargetRect] = useState(null)
  const welcomeStartedRef = useRef(false)

  const storageKey = useMemo(() => tourStorageKey(patient), [patient])
  const hideShopTab = patientHidesShopTab(patient)
  const tourSteps = useMemo(() => {
    if (!hideShopTab) return TOUR_STEPS
    return TOUR_STEPS
      .filter(step => step.id !== 'shop')
      .map(step => {
        if (step.id === 'welcome') {
          return {
            ...step,
            body: 'Your profile and consent are complete. This quick walkthrough shows where to upload labs, track orders, and message the care team.',
          }
        }
        if (step.id === 'next-steps') {
          return {
            ...step,
            body: 'Start with labs. Upload recent results if you have them, then watch your dashboard for the next item your care team needs.',
          }
        }
        if (step.id === 'labs') {
          return {
            ...step,
            body: 'If you already have recent labs, upload them here so your provider can review them. Lab updates and results live here.',
          }
        }
        return step
      })
  }, [hideShopTab])
  const safeStepIndex = Math.min(stepIndex, Math.max(0, tourSteps.length - 1))
  const step = tourSteps[safeStepIndex]
  const estimatedPanelHeight = step?.highlights?.length ? 390 : 260
  const position = targetRect ? panelPosition(targetRect, estimatedPanelHeight) : panelPosition(null, estimatedPanelHeight)
  const welcomeRequested = new URLSearchParams(location.search).get('welcome') === '1'

  const startTour = useCallback((force = false) => {
    if (!patient?.mso) return
    if (!force && readTourComplete(storageKey)) return
    setStepIndex(0)
    setOpen(true)
  }, [patient?.mso, storageKey])

  const finishTour = useCallback((options = {}) => {
    writeTourComplete(storageKey)
    setOpen(false)
    if (options.returnHome && location.pathname !== '/dashboard') {
      navigate('/dashboard')
    }
  }, [location.pathname, navigate, storageKey])

  useEffect(() => {
    function onStart() {
      startTour(true)
    }
    window.addEventListener('kis:portal-tour:start', onStart)
    return () => window.removeEventListener('kis:portal-tour:start', onStart)
  }, [startTour])

  useEffect(() => {
    if (!patient?.mso || !welcomeRequested || welcomeStartedRef.current) return
    welcomeStartedRef.current = true
    startTour(true)

    const params = new URLSearchParams(location.search)
    params.delete('welcome')
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    }, { replace: true })
  }, [location.pathname, location.search, navigate, patient?.mso, startTour, welcomeRequested])

  useEffect(() => {
    if (!open || !step?.path || location.pathname === step.path) return
    navigate(step.path)
  }, [location.pathname, navigate, open, step])

  useEffect(() => {
    if (!open) return undefined

    let frame = 0
    const updateRect = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        const nextRect = visibleRectFor(step?.target)
        setTargetRect(nextRect)
      })
    }

    const timer = window.setTimeout(updateRect, 80)
    window.addEventListener('resize', updateRect)
    window.addEventListener('scroll', updateRect, true)
    return () => {
      window.clearTimeout(timer)
      window.cancelAnimationFrame(frame)
      window.removeEventListener('resize', updateRect)
      window.removeEventListener('scroll', updateRect, true)
    }
  }, [location.pathname, open, step])

  if (!open || !step) return null

  const isLast = safeStepIndex === tourSteps.length - 1
  const runStepAction = () => {
    if (!step.actionEvent) return
    finishTour()
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(step.actionEvent, { detail: step.actionDetail || {} }))
    }, 0)
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Portal walkthrough" style={{ position: 'fixed', inset: 0, zIndex: 160, pointerEvents: 'none' }}>
      {targetRect ? (
        <div style={{
          position: 'fixed',
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
          borderRadius: 18,
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.48), 0 20px 48px rgba(15, 23, 42, 0.22)',
          border: `2px solid ${C.green}`,
          pointerEvents: 'none',
          transition: 'all 0.18s ease',
        }} />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.48)' }} />
      )}

      <section style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: position.width,
        borderRadius: 20,
        border: '1px solid rgba(255,255,255,0.72)',
        background: 'rgba(255,255,255,0.98)',
        boxShadow: '0 24px 80px rgba(15, 23, 42, 0.24)',
        padding: 18,
        maxHeight: 'calc(100vh - 28px)',
        overflowY: 'auto',
        pointerEvents: 'auto',
        transition: 'top 0.18s ease, left 0.18s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 820, color: C.greenDark, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Step {safeStepIndex + 1} of {tourSteps.length}
            </div>
            <h2 style={{ margin: '6px 0 0', fontSize: 20, lineHeight: 1.18, color: C.text, fontWeight: 860 }}>
              {step.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={finishTour}
            aria-label="Close walkthrough"
            style={{
              width: 34,
              height: 34,
              border: 'none',
              borderRadius: 17,
              background: '#F2F2F7',
              color: C.textSecondary,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X size={17} />
          </button>
        </div>

        <p style={{ margin: '12px 0 18px', color: C.textSecondary, fontSize: 14.2, lineHeight: 1.48 }}>
          {step.body}
        </p>

        {step.highlights?.length ? (
          <div style={{
            margin: '-4px 0 18px',
            border: '1px solid #A7E7CC',
            borderRadius: 16,
            background: '#EAF8F1',
            padding: 12,
            display: 'grid',
            gap: 8,
          }}>
            {step.highlights.map(item => (
              <div key={item} style={{ display: 'grid', gridTemplateColumns: '18px minmax(0, 1fr)', gap: 8, alignItems: 'start' }}>
                <span style={{ width: 18, height: 18, borderRadius: 9, background: C.green, color: '#FFFFFF', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 }}>
                  <Check size={12} strokeWidth={2.6} />
                </span>
                <span style={{ color: C.greenDark, fontSize: 13.1, lineHeight: 1.38, fontWeight: 720 }}>{item}</span>
              </div>
            ))}
          </div>
        ) : null}

        {step.actionEvent && (
          <button
            type="button"
            onClick={runStepAction}
            style={{
              width: '100%',
              minHeight: 40,
              border: 'none',
              borderRadius: 20,
              background: C.blue,
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontWeight: 820,
              fontSize: 13.5,
              margin: '-2px 0 14px',
              boxShadow: '0 10px 24px rgba(0, 122, 255, 0.22)',
            }}
          >
            <MessageCircle size={16} strokeWidth={2.35} />
            {step.actionLabel}
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <button
            type="button"
            onClick={() => setStepIndex(Math.max(0, safeStepIndex - 1))}
            disabled={safeStepIndex === 0}
            style={{
              minWidth: 86,
              height: 38,
              border: `1px solid ${C.border}`,
              borderRadius: 19,
              background: '#FFFFFF',
              color: safeStepIndex === 0 ? C.textTertiary : C.text,
              cursor: safeStepIndex === 0 ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              fontWeight: 760,
              fontSize: 13,
            }}
          >
            <ArrowLeft size={15} />
            Back
          </button>

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }} aria-hidden="true">
            {tourSteps.map((item, index) => (
              <span
                key={item.id}
                style={{
                  width: index === safeStepIndex ? 18 : 7,
                  height: 7,
                  borderRadius: 4,
                  background: index === safeStepIndex ? C.green : '#D1D1D6',
                  transition: 'width 0.18s ease',
                }}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              if (isLast) finishTour({ returnHome: true })
              else setStepIndex(Math.min(tourSteps.length - 1, safeStepIndex + 1))
            }}
            style={{
              minWidth: 96,
              height: 38,
              border: 'none',
              borderRadius: 19,
              background: C.green,
              color: '#FFFFFF',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 7,
              fontWeight: 800,
              fontSize: 13,
              boxShadow: '0 10px 24px rgba(0, 191, 109, 0.24)',
            }}
          >
            {isLast ? <Check size={15} /> : <ArrowRight size={15} />}
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      </section>
    </div>
  )
}
